/* =============================================================================
 * Copyright (c) 2026 SKYXOPS Corp. All rights reserved.
 * ============================================================================= */
import { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Skeleton, syncThemeFromProfile } from '@skyxops/ui';
import { LoginPage } from './pages/LoginPage';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { MarketplaceRegisterPage } from './pages/MarketplaceRegisterPage';
import { ProfilePage } from './pages/ProfilePage';
import { TileGridPage } from './pages/TileGridPage';
import { AppCommandPalette } from './components/AppCommandPalette';
import { ApiError, Branding, getBranding, getProfile, logout as apiLogout, whoami } from './api';

const GalleryPage = lazy(() =>
  import('./pages/GalleryPage').then((m) => ({ default: m.GalleryPage })),
);

export function App() {
  const [authReady, setAuthReady] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [branding, setBranding] = useState<Branding | null>(null);

  // On mount, ping /api/v1/launcher/me to see if there's a valid session.
  // AI-XMOD-70 / AI-LAUNCH-24 — harmonized cookie-first bootstrap across
  // launcher / msp / orbit. Always attempt whoami: in deployed envs the HttpOnly
  // session cookie rides credentials:'include'; in localhost dev the in-memory
  // ?token= drives the Bearer header.
  //   - 401 → expected (no session); land on /login silently.
  //   - other ApiError / network → console.error so devs see real bugs.
  useEffect(() => {
    Promise.all([
      whoami().then(() => true).catch((e) => {
        if (e instanceof ApiError && e.status === 401) {
          // Expected — silent route to /login.
        } else {
          console.error('[launcher] whoami failed (non-auth)', e);
        }
        return false;
      }),
      getBranding().catch((e) => {
        console.warn('[launcher] getBranding failed', e);
        return null;
      }),
    ])
      .then(([ok, brandingVal]) => {
        setAuthed(ok);
        setBranding(brandingVal);
        // AI-LAUNCH-27 — the user's theme follows them across apps: apply the
        // profile theme_preference on load (best-effort, local theme until then).
        if (ok) void syncThemeFromProfile(() => getProfile().then((p) => p.theme_preference));
      })
      .finally(() => setAuthReady(true));
  }, []);

  useEffect(() => {
    if (branding) {
      document.documentElement.style.setProperty('--brand-primary', branding.primary_color);
    }
  }, [branding]);

  // AI-MSP-124 — branding follows the signed-in user. Refetch on login (so the
  // white-label shows without a manual refresh) and reset on logout (so the
  // previous user's brand, e.g. a reseller's, never persists into the next session).
  useEffect(() => {
    if (authed) {
      getBranding().then(setBranding).catch(() => { /* keep current */ });
    } else {
      setBranding(null);
      document.documentElement.style.removeProperty('--brand-primary');
    }
  }, [authed]);

  // AI-LAUNCH-24 — after logout the browser back-forward cache can restore this
  // authed page (with its in-memory token). On a bfcache restore, force a fresh
  // load so the auth bootstrap re-runs against the (now-cleared) cookie and
  // lands on /login if signed out. Pairs with Cache-Control: no-store on index.html.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) window.location.reload();
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  if (!authReady) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  return (
    <>
      {/* ⌘K command palette — mounted once in the authed shell (console-pro
          B-common). Gated on `authed` so it (and its getTiles fetch) stay off
          the login / accept-invite screens. */}
      {authed && <AppCommandPalette />}
      <Routes>
      <Route path="/login" element={<LoginPage onAuthed={() => setAuthed(true)} />} />
      <Route path="/reset" element={<LoginPage onAuthed={() => setAuthed(true)} />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      {/* AI-LAUNCH-59 - AWS Marketplace landing. Public by necessity: the buyer
          has no account yet. The page itself is inert without a verified session. */}
      <Route path="/marketplace/register" element={<MarketplaceRegisterPage />} />
      {/* /security consolidated into /profile › Security — redirect old bookmarks. */}
      <Route path="/security" element={authed ? <Navigate to="/profile#security" replace /> : <Navigate to="/login" replace />} />
      <Route
        path="/profile"
        element={
          authed ? (
            <ProfilePage branding={branding} onLogout={() => { void apiLogout().finally(() => setAuthed(false)); }} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/"
        element={
          authed ? (
            <TileGridPage branding={branding} onLogout={() => { void apiLogout().finally(() => setAuthed(false)); }} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/dev/gallery"
        element={
          authed ? (
            <Suspense fallback={<Skeleton className="h-32 w-full" />}>
              <GalleryPage
                principal={null}
                branding={branding}
                onLogout={() => { void apiLogout().finally(() => setAuthed(false)); }}
              />
            </Suspense>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
